// A polygon's perimeter line is derived by the backend and re-copied from the polygon on every save.
// The store enforces the same invariant locally so the line follows the area at once and never drifts
// through an edit of its own: its points are always the polygon's closed outline.

import type { linearLineType, polygonType } from './types'

type Point = { x: number; y: number }

// Takes any object: callers pass full lines, tree members and canvas placeholder objects, and it is
// also handed straight to Array#find.
export const isLinkedPerimeter = (line: object): boolean => {
  const l = line as Partial<linearLineType>
  return l.geometryLinked === true && l.derivedShapeKind === 'POLYGON_PERIMETER' && !!l.derivedFromShapeId
}

/**
 * Whether a locked perimeter is part of the current line selection. A perimeter traces its area
 * exactly, so a right-click on it also lands on the area's own vertices and edges: adding a point
 * there would edit, and hand back a handle on, a shape the user did not select.
 */
export const perimeterIsSelected = (lines: { id?: string }[], selectedLineIds: readonly string[]): boolean =>
  selectedLineIds.some((id) => {
    const line = lines.find((l) => l.id === id)
    return !!line && isLinkedPerimeter(line)
  })

/** The outline with its first point repeated at the end, as the backend stores a perimeter. */
export const closedRing = <P extends Point>(points: P[]): P[] => {
  if (points.length === 0) return []
  const first = points[0]
  const last = points[points.length - 1]
  return first.x === last.x && first.y === last.y ? points : [...points, first]
}

/** Perimeters follow their polygon; a perimeter whose polygon is gone is dropped; other lines pass through. */
export const syncLinkedPerimeters = (lines: linearLineType[], polygons: polygonType[]): linearLineType[] => {
  const polygonsById = new Map(polygons.map((p) => [p.id, p]))
  const out: linearLineType[] = []
  for (const line of lines) {
    if (!isLinkedPerimeter(line)) {
      out.push(line)
      continue
    }
    const source = polygonsById.get(line.derivedFromShapeId as string)
    if (source) out.push({ ...line, points: closedRing(source.points) })
  }
  return out
}
